import { isTruthyAndNotEmpty } from '@stimcar/libs-kernel';
import type { StudioCapabilityId } from '../host/auth/capabilities.js';
import type {
  AutomationRunRecord,
  AutomationsListView,
  SessionAutomationView,
} from '../host/automations/types.js';
import type {
  HostCheckoutSyncResult,
  HostPreviewStatus,
  PromptActivityEntry,
  PromptActivityView,
} from '../host/types.js';
import { StudioBackendRoutes, studioRouteWithQuery } from '../studioRoutes.js';

export type { PromptActivityEntry, PromptActivityView };

export type StudioPublicSession = {
  readonly email: string;
  readonly givenName?: string;
  readonly capabilities: readonly StudioCapabilityId[];
};

export type SessionPreviewView = {
  readonly status: 'live' | 'off';
  readonly url: string | undefined;
};

export type ChatAttachmentKind = 'image' | 'file';

export type ChatAttachmentView = {
  readonly id: string;
  readonly fileName: string;
  readonly mimeType: string;
  readonly kind: ChatAttachmentKind;
  readonly url: string;
};

export type IncomingAttachment = {
  readonly fileName: string;
  readonly mimeType: string;
  readonly data: string;
};

export type StudioChatMessage = {
  readonly id: string;
  readonly displayName: string;
  readonly role: 'user' | 'assistant' | 'system';
  readonly text: string;
  readonly createdAt?: number;
  readonly authorEmail?: string;
  readonly attachments?: readonly ChatAttachmentView[];
};

export type AgentProgressKind = 'thinking' | 'tool' | 'assistant' | 'status';

export type AgentProgressStep = {
  readonly kind: AgentProgressKind;
  readonly text: string;
};

export type DeployParentKind = 'template' | 'atelier' | 'deployed';

export type DeployParentRef = {
  readonly kind: DeployParentKind;
  readonly slug: string;
  readonly targetId: string;
  readonly deployedId?: string;
};

export type DeployMode = 'replace' | 'new';

export type DeploySessionRequest = {
  readonly mode: DeployMode;
  readonly newSlug?: string;
};

export type SessionTitleSource = 'provisional' | 'ai' | 'user';

export type SessionVisibility = 'private' | 'open';

export type AgentModelOptionView = {
  readonly id: string;
  readonly label: string;
};

export type AgentProviderId = 'cursor' | 'codex';

export type SessionView = {
  readonly id: string;
  readonly urlPath: string;
  readonly title: string;
  readonly titleSource?: SessionTitleSource;
  readonly messages: readonly StudioChatMessage[];
  readonly preview: SessionPreviewView;
  readonly mergeRequestUrl: string | undefined;
  readonly openspecArchiveMergeRequestUrl: string | undefined;
  readonly agentBusy: boolean;
  readonly progress: readonly AgentProgressStep[];
  /** Live browser-agent screenshot while the turn is busy (cache-busted). */
  readonly browserPreviewUrl?: string;
  readonly browserResultsUrl?: string;
  readonly teach?: {
    readonly status: 'idle' | 'recording' | 'finalizing' | 'ready' | 'failed';
    readonly recordingId?: string;
    readonly viewerUrl?: string;
    readonly frameUrl?: string;
    readonly summary?: string;
    readonly promotedSkillName?: string;
    readonly error?: string;
    readonly enabled: boolean;
  };
  readonly sourceConnect?: {
    readonly enabled: boolean;
    readonly viewerUrl?: string;
    readonly frameUrl?: string;
    readonly activeSource?: string;
    readonly browserOwner?: 'agent' | 'teach' | 'connect';
    readonly sources: Readonly<
      Record<string, { readonly state: string; readonly message: string }>
    >;
  };
  readonly agentProvider: AgentProviderId;
  readonly agentModelId: string;
  readonly agentModels: readonly AgentModelOptionView[];
  readonly branch: string;
  readonly targetId: string;
  readonly targetLabel: string;
  readonly parentRef?: DeployParentRef;
  readonly favorited?: boolean;
  readonly thumbnailCaptureRecommended: boolean;
  readonly visibility: SessionVisibility;
  readonly sharedWithEmails: readonly string[];
  readonly canManageSharing?: boolean;
};

export type AgentStatusView = {
  readonly status: 'ok';
  readonly busySessionCount: number;
  readonly busySessionIds: readonly string[];
  readonly agentProvider: AgentProviderId;
  readonly defaultAgentModelId: string;
  readonly agentModels: readonly AgentModelOptionView[];
};

export type FavoriteSessionSummaryView = {
  readonly sessionId: string;
  readonly urlPath: string;
  readonly title: string;
  readonly targetId: string;
  readonly targetLabel: string;
  readonly description: string;
  readonly livePreviewUrl: string | undefined;
  readonly thumbnailUrl: string | undefined;
};

export type SessionSummaryView = {
  readonly id: string;
  readonly urlPath: string;
  readonly title: string;
  readonly createdByEmail: string;
  readonly createdAt: number;
  readonly lastActivityAt: number;
  readonly targetId: string;
  readonly targetLabel: string;
  readonly visibility?: SessionVisibility;
};

export type StudioTargetSummaryView = {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly kind: string;
  readonly previewAvailable: boolean;
  readonly hostPreviewUrl: string;
  readonly resumeSessionId?: string;
};

export type DeployedAppSummaryView = {
  readonly id: string;
  readonly slug: string;
  readonly hostname: string;
  readonly url: string;
  readonly targetId: string;
  readonly label: string;
  readonly updatedAt: number;
  readonly sourceSessionId?: string;
  readonly gitlabProjectPath?: string;
  readonly parentRef?: DeployParentRef;
};

export type DeployedAppCommitSummaryView = {
  readonly sha: string;
  readonly shortSha: string;
  readonly title: string;
  readonly committedAt: string;
};

export type ApiErrorBody = {
  readonly error?: string;
};

export type StudioApiErrorKind = 'unreachable' | 'http';

export class StudioApiError extends Error {
  public readonly kind: StudioApiErrorKind;
  public readonly status: number | undefined;

  public constructor(kind: StudioApiErrorKind, message: string, status?: number) {
    super(message);
    this.name = 'StudioApiError';
    this.kind = kind;
    this.status = status;
  }
}

async function studioRequest(url: string, init?: RequestInit): Promise<Response> {
  const outcome = await fetch(url, {
    ...init,
    credentials: 'same-origin',
  }).then(
    (response) => ({ ok: true as const, response }),
    () => ({ ok: false as const })
  );
  if (!outcome.ok) {
    throw new StudioApiError('unreachable', '');
  }
  return outcome.response;
}

function httpErrorMessage(status: number, text: string): string {
  const trimmed = text.trim();
  if (trimmed.startsWith('{')) {
    const body: unknown = JSON.parse(trimmed);
    if (body !== null && typeof body === 'object' && 'error' in body) {
      const errorBody = body as ApiErrorBody;
      if (typeof errorBody.error === 'string' && isTruthyAndNotEmpty(errorBody.error)) {
        return errorBody.error;
      }
    }
  }
  if (isTruthyAndNotEmpty(trimmed)) {
    return trimmed;
  }
  return `HTTP ${String(status)}`;
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }
  const text = await response.text();
  if (!response.ok) {
    throw new StudioApiError('http', httpErrorMessage(response.status, text), response.status);
  }
  if (!isTruthyAndNotEmpty(text.trim())) {
    return undefined as T;
  }
  return JSON.parse(text) as T;
}

export function normalizeSessionView(view: SessionView): SessionView {
  const agentModels = Array.isArray(view.agentModels) ? view.agentModels : [];
  const agentProvider = view.agentProvider === 'codex' ? 'codex' : 'cursor';
  const agentModelId =
    typeof view.agentModelId === 'string' && view.agentModelId !== ''
      ? view.agentModelId
      : (agentModels[0]?.id ?? '');
  return {
    ...view,
    title: typeof view.title === 'string' ? view.title : '',
    titleSource:
      view.titleSource === 'provisional' || view.titleSource === 'ai' || view.titleSource === 'user'
        ? view.titleSource
        : undefined,
    messages: (view.messages ?? []).map((message) => ({
      ...message,
      attachments: message.attachments ?? [],
    })),
    progress: view.progress ?? [],
    ...(typeof view.browserPreviewUrl === 'string' && view.browserPreviewUrl !== ''
      ? { browserPreviewUrl: view.browserPreviewUrl }
      : {}),
    ...(typeof view.browserResultsUrl === 'string' && view.browserResultsUrl !== ''
      ? { browserResultsUrl: view.browserResultsUrl }
      : {}),
    ...(view.teach !== undefined && view.teach !== null && typeof view.teach === 'object'
      ? { teach: view.teach }
      : {}),
    agentProvider,
    agentModelId,
    agentModels,
    thumbnailCaptureRecommended: view.thumbnailCaptureRecommended === true,
    visibility: view.visibility === 'open' ? 'open' : 'private',
    sharedWithEmails: Array.isArray(view.sharedWithEmails) ? view.sharedWithEmails : [],
    ...(view.favorited === true || view.favorited === false ? { favorited: view.favorited } : {}),
    ...(view.canManageSharing === true || view.canManageSharing === false
      ? { canManageSharing: view.canManageSharing }
      : {}),
  };
}

/** Keep prior pin flags when a SessionView response omits them. */
export function mergeSessionFavoriteFlags(
  previous: SessionView | undefined,
  next: SessionView
): SessionView {
  return {
    ...next,
    favorited: next.favorited ?? previous?.favorited,
    thumbnailCaptureRecommended:
      next.thumbnailCaptureRecommended || previous?.thumbnailCaptureRecommended === true,
  };
}

export async function loadStudioSession(signal?: AbortSignal): Promise<StudioPublicSession | null> {
  const response = await studioRequest(StudioBackendRoutes.AUTH_SESSION, {
    signal,
  });
  if (response.status === 401) {
    return null;
  }
  return parseResponse<StudioPublicSession>(response);
}

export async function fetchHostPreviewStatus(targetId?: string): Promise<HostPreviewStatus> {
  const response = await studioRequest(
    studioRouteWithQuery(StudioBackendRoutes.HOST_PREVIEW, { targetId })
  );
  return parseResponse<HostPreviewStatus>(response);
}

export async function syncHostPreview(): Promise<HostCheckoutSyncResult> {
  const response = await studioRequest(StudioBackendRoutes.HOST_PREVIEW_SYNC, {
    method: 'POST',
  });
  return parseResponse<HostCheckoutSyncResult>(response);
}

/** @deprecated Prefer syncHostPreview */
export async function syncHostMarketplacePreview(): Promise<HostCheckoutSyncResult> {
  return syncHostPreview();
}

export type CreateSessionRequest = {
  readonly targetId?: string;
  readonly agentModelId?: string;
};

export type CreateScratchAppRequest = {
  readonly slug: string;
};

export async function listTargets(): Promise<readonly StudioTargetSummaryView[]> {
  const response = await studioRequest(StudioBackendRoutes.TARGETS);
  const body = await parseResponse<readonly StudioTargetSummaryView[]>(response);
  return body ?? [];
}

export async function listFavorites(): Promise<readonly FavoriteSessionSummaryView[]> {
  const response = await studioRequest(StudioBackendRoutes.FAVORITES);
  const body = await parseResponse<readonly FavoriteSessionSummaryView[]>(response);
  return body ?? [];
}

export async function listDeployments(): Promise<readonly DeployedAppSummaryView[]> {
  const response = await studioRequest(StudioBackendRoutes.DEPLOYMENTS);
  const body = await parseResponse<readonly DeployedAppSummaryView[]>(response);
  return body ?? [];
}

export async function deploySession(
  sessionId: string,
  request: DeploySessionRequest = { mode: 'replace' }
): Promise<DeployedAppSummaryView> {
  const response = await studioRequest(StudioBackendRoutes.SESSION_DEPLOY(sessionId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  return parseResponse<DeployedAppSummaryView>(response);
}

export async function stopDeployment(slug: string): Promise<void> {
  const response = await studioRequest(StudioBackendRoutes.DEPLOYMENT(slug), {
    method: 'DELETE',
  });
  await parseResponse<void>(response);
}

export async function listDeploymentCommits(
  slug: string
): Promise<readonly DeployedAppCommitSummaryView[]> {
  const response = await studioRequest(StudioBackendRoutes.DEPLOYMENT_COMMITS(slug));
  const body = await parseResponse<readonly DeployedAppCommitSummaryView[]>(response);
  return body ?? [];
}

export async function revertDeployment(slug: string, sha: string): Promise<DeployedAppSummaryView> {
  const response = await studioRequest(StudioBackendRoutes.DEPLOYMENT_REVERT(slug), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sha }),
  });
  return parseResponse<DeployedAppSummaryView>(response);
}

export async function fetchLastIndependentSession(): Promise<SessionSummaryView | null> {
  const response = await studioRequest(StudioBackendRoutes.LAST_INDEPENDENT_SESSION);
  return parseResponse<SessionSummaryView | null>(response);
}

export async function pinFavorite(sessionId: string): Promise<SessionView> {
  const response = await studioRequest(StudioBackendRoutes.FAVORITE(sessionId), {
    method: 'PUT',
  });
  return parseSessionView(response);
}

export async function unpinFavorite(sessionId: string): Promise<SessionView> {
  const response = await studioRequest(StudioBackendRoutes.FAVORITE(sessionId), {
    method: 'DELETE',
  });
  return parseSessionView(response);
}

export async function uploadSessionThumbnail(sessionId: string, file: File): Promise<void> {
  const response = await studioRequest(StudioBackendRoutes.SESSION_THUMBNAIL(sessionId), {
    method: 'PUT',
    headers: {
      'Content-Type': file.type.trim() !== '' ? file.type : 'image/png',
    },
    body: file,
  });
  await parseResponse<void>(response);
}

export async function startTeachBrowser(sessionId: string): Promise<SessionView> {
  const response = await studioRequest(StudioBackendRoutes.SESSION_TEACH_START(sessionId), {
    method: 'POST',
  });
  return parseSessionView(response);
}

export async function stopTeachBrowser(sessionId: string): Promise<SessionView> {
  const response = await studioRequest(StudioBackendRoutes.SESSION_TEACH_STOP(sessionId), {
    method: 'POST',
  });
  return parseSessionView(response);
}

export async function promoteTeachBrowser(sessionId: string): Promise<SessionView> {
  const response = await studioRequest(StudioBackendRoutes.SESSION_TEACH_PROMOTE(sessionId), {
    method: 'POST',
  });
  return parseSessionView(response);
}

export async function discardTeachBrowser(sessionId: string): Promise<SessionView> {
  const response = await studioRequest(StudioBackendRoutes.SESSION_TEACH_DISCARD(sessionId), {
    method: 'POST',
  });
  return parseSessionView(response);
}

export async function startSourceConnect(sessionId: string, source: string): Promise<SessionView> {
  const response = await studioRequest(StudioBackendRoutes.SESSION_SOURCES_CONNECT(sessionId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ source }),
  });
  return parseSessionView(response);
}

export async function skipSourceConnect(sessionId: string, source: string): Promise<SessionView> {
  const response = await studioRequest(StudioBackendRoutes.SESSION_SOURCES_SKIP(sessionId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ source }),
  });
  return parseSessionView(response);
}

export async function createSession(input: CreateSessionRequest = {}): Promise<SessionView> {
  const body: Record<string, string> = {};
  if (input.targetId !== undefined) {
    body.targetId = input.targetId;
  }
  if (input.agentModelId !== undefined) {
    body.agentModelId = input.agentModelId;
  }
  const response = await studioRequest(StudioBackendRoutes.SESSIONS, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return parseSessionView(response);
}

export async function fetchAgentStatus(): Promise<AgentStatusView> {
  const response = await studioRequest(StudioBackendRoutes.AGENT_STATUS);
  return parseResponse<AgentStatusView>(response);
}

export async function setSessionAgentModel(
  sessionId: string,
  agentModelId: string
): Promise<SessionView> {
  const response = await studioRequest(StudioBackendRoutes.SESSION(sessionId), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ agentModelId }),
  });
  return parseSessionView(response);
}

export async function createScratchApp(
  input: CreateScratchAppRequest
): Promise<StudioTargetSummaryView> {
  const response = await studioRequest(StudioBackendRoutes.SCRATCH_APP_SESSIONS, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ slug: input.slug }),
  });
  return parseResponse<StudioTargetSummaryView>(response);
}

export async function deleteScratchApp(slug: string): Promise<void> {
  const response = await studioRequest(StudioBackendRoutes.SCRATCH_APP(slug), {
    method: 'DELETE',
  });
  await parseResponse<void>(response);
}

export async function listSessions(targetId?: string): Promise<readonly SessionSummaryView[]> {
  const response = await studioRequest(
    studioRouteWithQuery(StudioBackendRoutes.SESSIONS, { targetId })
  );
  const body = await parseResponse<readonly SessionSummaryView[]>(response);
  return body ?? [];
}

export async function fetchSession(sessionId: string): Promise<SessionView> {
  const response = await studioRequest(StudioBackendRoutes.SESSION(sessionId));
  return parseSessionView(response);
}

export async function renameSessionTitle(sessionId: string, title: string): Promise<SessionView> {
  const response = await studioRequest(StudioBackendRoutes.SESSION(sessionId), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title }),
  });
  return parseSessionView(response);
}

export type UpdateSessionSharingRequest = {
  readonly visibility?: SessionVisibility;
  readonly sharedWithEmails?: readonly string[];
};

export async function updateSessionSharing(
  sessionId: string,
  input: UpdateSessionSharingRequest
): Promise<SessionView> {
  const response = await studioRequest(StudioBackendRoutes.SESSION_SHARING(sessionId), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return parseSessionView(response);
}

export async function postSessionMessage(
  sessionId: string,
  text: string,
  attachments: readonly IncomingAttachment[] = []
): Promise<SessionView> {
  const response = await studioRequest(StudioBackendRoutes.SESSION_MESSAGES(sessionId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, attachments }),
  });
  return parseSessionView(response);
}

export async function startSessionPreview(sessionId: string): Promise<SessionView> {
  const response = await studioRequest(StudioBackendRoutes.SESSION_PREVIEW(sessionId), {
    method: 'POST',
  });
  return parseSessionView(response);
}

export async function touchSessionPreview(sessionId: string): Promise<void> {
  const response = await studioRequest(StudioBackendRoutes.SESSION_TOUCH(sessionId), {
    method: 'POST',
  });
  await parseResponse<void>(response);
}

export async function openSessionMergeRequest(sessionId: string): Promise<SessionView> {
  const response = await studioRequest(StudioBackendRoutes.SESSION_MERGE_REQUEST(sessionId), {
    method: 'POST',
  });
  return parseSessionView(response);
}

export async function syncSessionBase(sessionId: string): Promise<SessionView> {
  const response = await studioRequest(StudioBackendRoutes.SESSION_SYNC_BASE(sessionId), {
    method: 'POST',
  });
  return parseSessionView(response);
}

export async function cancelSessionTurn(sessionId: string): Promise<SessionView> {
  const response = await studioRequest(StudioBackendRoutes.SESSION_CANCEL(sessionId), {
    method: 'POST',
  });
  return parseSessionView(response);
}

async function parseSessionView(response: Response): Promise<SessionView> {
  const view = await parseResponse<SessionView>(response);
  return normalizeSessionView(view);
}

export async function abandonSession(sessionId: string): Promise<void> {
  const response = await studioRequest(StudioBackendRoutes.SESSION_ABANDON(sessionId), {
    method: 'POST',
  });
  await parseResponse<void>(response);
}

export async function fetchAutomations(): Promise<AutomationsListView> {
  const response = await studioRequest(StudioBackendRoutes.AUTOMATIONS);
  return parseResponse<AutomationsListView>(response);
}

export async function createAutomation(input: {
  readonly title: string;
  readonly cron: string;
  readonly prompt: string;
  readonly parentSessionId: string;
  readonly proposeMergeRequest?: boolean;
}): Promise<SessionAutomationView> {
  const response = await studioRequest(StudioBackendRoutes.AUTOMATIONS, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return parseResponse<SessionAutomationView>(response);
}

export async function updateAutomation(
  id: string,
  input: {
    readonly title?: string;
    readonly cron?: string;
    readonly prompt?: string;
    readonly proposeMergeRequest?: boolean;
  }
): Promise<SessionAutomationView> {
  const response = await studioRequest(StudioBackendRoutes.AUTOMATION(id), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return parseResponse<SessionAutomationView>(response);
}

export async function deleteAutomation(id: string): Promise<void> {
  const response = await studioRequest(StudioBackendRoutes.AUTOMATION(id), {
    method: 'DELETE',
  });
  await parseResponse<void>(response);
}

export async function setAutomationEnabled(id: string, enabled: boolean): Promise<void> {
  const response = await studioRequest(StudioBackendRoutes.AUTOMATION_ENABLE(id), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled }),
  });
  await parseResponse<{ readonly ok: boolean }>(response);
}

export async function runAutomation(id: string): Promise<AutomationRunRecord> {
  const response = await studioRequest(StudioBackendRoutes.AUTOMATION_RUN(id), {
    method: 'POST',
  });
  return parseResponse<AutomationRunRecord>(response);
}

export async function fetchPromptActivity(signal?: AbortSignal): Promise<PromptActivityView> {
  const response = await studioRequest(StudioBackendRoutes.PROMPT_ACTIVITY, { signal });
  return parseResponse<PromptActivityView>(response);
}
